/**
 * Oath Discharge Service
 * API calls for discharge operations using the Oath authentication
 * Routes to /oath/discharge/* endpoints
 *
 * All endpoints require JWT authentication with Oath auth type
 * and UPDATE:POWER:DISCHARGE permission.
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const USE_MOCK = false;

// ============================================
// AUTH HELPERS (same pattern as oathPowerReportService.js)
// ============================================

const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// MOCK DATA
// ============================================

// Mirrors SPECIAL_LIMIT_STATES in components/app/discharges/utils/constants.js.
// Kept local so this service does not import from a component folder.
const RAISED_LIMIT_STATES = ['CA', 'CT'];

const MOCK_POWERS = [
  { powerNo: 'FL-E-001', defendantName: 'Smith, John', bondAmount: 5000, powerStatus: 'E', state: 'FL', county: 'Miami-Dade', executionDate: '2025-08-15', isForfeiture: false, batchClosed: true },
  { powerNo: 'FL-E-002', defendantName: 'Johnson, Maria', bondAmount: 10000, powerStatus: 'E', state: 'FL', county: 'Broward', executionDate: '2025-07-20', isForfeiture: false, batchClosed: true },
  { powerNo: 'CA-E-003', defendantName: 'Williams, Robert', bondAmount: 75000, powerStatus: 'E', state: 'CA', county: 'Los Angeles', executionDate: '2025-06-10', isForfeiture: false, batchClosed: true },
  { powerNo: 'CA-E-004', defendantName: 'Brown, Jennifer', bondAmount: 150000, powerStatus: 'E', state: 'CA', county: 'San Diego', executionDate: '2025-09-01', isForfeiture: false, batchClosed: true },
  { powerNo: 'TX-F-005', defendantName: 'Davis, Michael', bondAmount: 25000, powerStatus: 'F', state: 'TX', county: 'Harris', executionDate: '2025-05-22', isForfeiture: true, batchClosed: true },
  { powerNo: 'NC-F-006', defendantName: 'Garcia, Ana', bondAmount: 15000, powerStatus: 'F', state: 'NC', county: 'Mecklenburg', executionDate: '2025-04-18', isForfeiture: true, batchClosed: true },
  { powerNo: 'FL-J-007', defendantName: 'Martinez, Carlos', bondAmount: 30000, powerStatus: 'J', state: 'FL', county: 'Palm Beach', executionDate: '2025-03-05', isForfeiture: false, batchClosed: true },
  { powerNo: 'FL-E-008', defendantName: 'Wilson, Sarah', bondAmount: 8000, powerStatus: 'E', state: 'FL', county: 'Hillsborough', executionDate: '2025-10-12', isForfeiture: false, batchClosed: true },
  { powerNo: 'CA-F-009', defendantName: 'Anderson, David', bondAmount: 30000, powerStatus: 'F', state: 'CA', county: 'Orange', executionDate: '2025-02-28', isForfeiture: true, batchClosed: true },
  { powerNo: 'FL-E-010', defendantName: 'Thomas, Lisa', bondAmount: 12000, powerStatus: 'E', state: 'FL', county: 'Duval', executionDate: '2025-11-01', isForfeiture: false, batchClosed: true },
  { powerNo: 'FL-E-011', defendantName: 'Taylor, James', bondAmount: 3500, powerStatus: 'E', state: 'FL', county: 'Orange', executionDate: '2025-08-30', isForfeiture: false, batchClosed: true },
  { powerNo: 'CA-E-012', defendantName: 'Moore, Patricia', bondAmount: 45000, powerStatus: 'E', state: 'CA', county: 'Riverside', executionDate: '2025-07-15', isForfeiture: false, batchClosed: true },
  { powerNo: 'TX-E-013', defendantName: 'Jackson, William', bondAmount: 20000, powerStatus: 'E', state: 'TX', county: 'Dallas', executionDate: '2025-06-25', isForfeiture: false, batchClosed: true },
  { powerNo: 'FL-E-014', defendantName: 'Martin, Jessica', bondAmount: 6500, powerStatus: 'E', state: 'FL', county: 'Pinellas', executionDate: '2025-09-18', isForfeiture: false, batchClosed: true },
  { powerNo: 'NC-E-015', defendantName: 'Lee, Christopher', bondAmount: 9000, powerStatus: 'E', state: 'NC', county: 'Wake', executionDate: '2025-10-05', isForfeiture: false, batchClosed: true },
  { powerNo: 'FL-E-016', defendantName: 'White, Amanda', bondAmount: 4200, powerStatus: 'E', state: 'FL', county: 'Seminole', executionDate: '2025-11-20', isForfeiture: false, batchClosed: true },
  { powerNo: 'CA-J-017', defendantName: 'Harris, Daniel', bondAmount: 55000, powerStatus: 'J', state: 'CA', county: 'Sacramento', executionDate: '2025-05-10', isForfeiture: false, batchClosed: true },
  { powerNo: 'FL-E-018', defendantName: 'Clark, Michelle', bondAmount: 7500, powerStatus: 'E', state: 'FL', county: 'Volusia', executionDate: '2025-08-01', isForfeiture: false, batchClosed: true },
  { powerNo: 'TX-E-019', defendantName: 'Lewis, Brian', bondAmount: 35000, powerStatus: 'E', state: 'TX', county: 'Bexar', executionDate: '2025-04-22', isForfeiture: false, batchClosed: true },
  { powerNo: 'FL-E-020', defendantName: 'Robinson, Nicole', bondAmount: 11000, powerStatus: 'E', state: 'FL', county: 'Lee', executionDate: '2025-12-01', isForfeiture: false, batchClosed: true },
];

const generateMockValidation = (powerNo) => {
  const power = MOCK_POWERS.find((p) => p.powerNo === powerNo);
  if (!power) {
    return {
      powerNo,
      isValid: false,
      validationErrors: ['Power number not found'],
      requiresProof: false,
      proofReason: null,
      powerStatus: null,
      bondAmount: 0,
      defendantName: 'Unknown',
      state: null,
    };
  }

  let requiresProof = false;
  let proofReason = null;
  const isValid = true;

  if (power.powerStatus === 'J') {
    requiresProof = true;
    proofReason = 'Powers in judgment status require proof of discharge.';
  } else if (power.powerStatus === 'F' && ['NC', 'TX'].includes(power.state)) {
    requiresProof = true;
    proofReason = 'Forfeiture powers in NC/TX require proof of discharge.';
  } else if (power.powerStatus === 'F' && RAISED_LIMIT_STATES.includes(power.state) && power.bondAmount > 25000) {
    requiresProof = true;
    proofReason = `Forfeiture powers in ${power.state} > $25,000 require proof.`;
  } else if (power.powerStatus === 'F' && power.bondAmount > 10000) {
    requiresProof = true;
    proofReason = 'Forfeiture powers > $10,000 require proof.';
  } else if (RAISED_LIMIT_STATES.includes(power.state) && power.bondAmount > 100000) {
    requiresProof = true;
    proofReason = `Bonds in ${power.state} > $100,000 require proof.`;
  } else if (power.bondAmount > 50000) {
    requiresProof = true;
    proofReason = 'Bonds > $50,000 require proof.';
  }

  return {
    powerNo: power.powerNo,
    isValid,
    validationErrors: [],
    requiresProof,
    proofReason,
    powerStatus: power.powerStatus,
    bondAmount: power.bondAmount,
    defendantName: power.defendantName,
    state: power.state,
  };
};

let mockFileCounter = 0;

const mockDelay = (ms = 600) => new Promise((resolve) => setTimeout(resolve, ms));

// ============================================
// ELIGIBLE POWERS (Bulk prefetch)
// ============================================

// Singleton: cached prefetch result + in-flight promise (keyed by agencyId)
let _prefetchedPowers = null;   // { agencyId, powers: [], timestamp: number }
let _prefetchPromise = null;    // dedup concurrent calls
const PREFETCH_TTL_MS = 2 * 60_000; // 2 minutes

/**
 * Fetch all eligible dischargeable powers in one call (up to 500).
 * Results are cached per agencyId for PREFETCH_TTL_MS so repeat calls are free.
 *
 * @param {Object} [options]
 * @param {string|number} options.agencyId - Agency to fetch powers for (required)
 * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request
 * @returns {Promise<{success: boolean, data?: {powers: Array, count: number}, error?: string}>}
 */
export const fetchEligiblePowers = async (options = {}) => {
  const { signal, agencyId } = options;

  // Return cache if still fresh and same agency
  if (
    _prefetchedPowers &&
    _prefetchedPowers.agencyId === agencyId &&
    Date.now() - _prefetchedPowers.timestamp < PREFETCH_TTL_MS
  ) {
    return { success: true, data: { powers: _prefetchedPowers.powers, count: _prefetchedPowers.powers.length } };
  }

  // If agency changed, discard stale in-flight promise
  if (_prefetchPromise && _prefetchedPowers && _prefetchedPowers.agencyId !== agencyId) {
    _prefetchPromise = null;
  }

  // De-duplicate concurrent calls for the same agency
  if (_prefetchPromise) {
    return _prefetchPromise;
  }

  _prefetchPromise = (async () => {
    if (USE_MOCK) {
      await mockDelay(200);
      const powers = MOCK_POWERS.map((p) => ({
        powerNo: p.powerNo,
        status: p.powerStatus,
        bondAmount: p.bondAmount,
        defendantName: p.defendantName,
        state: p.state,
        county: p.county,
        isForfeiture: p.isForfeiture,
        batchClosed: p.batchClosed,
        agencyId: 100,
        agencyName: 'Mock Agency',
      }));
      _prefetchedPowers = { agencyId, powers, timestamp: Date.now() };
      return { success: true, data: { powers, count: powers.length } };
    }

    try {
      const params = new URLSearchParams({ take: '500' });
      if (agencyId) params.set('agencyId', agencyId);
      const response = await authFetch(`${BASE_URL}oath/discharge/powers?${params}`, { signal });
      const result = await parseResponse(response);

      if (!response.ok) {
        return { success: false, error: result.message || 'Failed to fetch eligible powers', data: null };
      }

      const powers = result.powers || result.data?.powers || [];
      _prefetchedPowers = { agencyId, powers, timestamp: Date.now() };
      return { success: true, data: { powers, count: powers.length } };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'cancelled', data: null };
      }
      console.error('Fetch eligible powers error:', error);
      return { success: false, error: 'Network error', data: null };
    }
  })();

  try {
    return await _prefetchPromise;
  } finally {
    _prefetchPromise = null;
  }
};

// Search cache: keyed by "agencyId|prefix", up to 20 entries, 2-min TTL
const _searchCache = new Map();
const SEARCH_CACHE_MAX = 20;
const SEARCH_CACHE_TTL_MS = 2 * 60_000;

const getSearchCacheKey = (prefix, agencyId) => `${agencyId || ''}|${prefix.toUpperCase()}`;

/**
 * Search eligible powers by prefix (calls API with powerNo param).
 * Results are cached so revisiting a prefix (e.g. after selecting, then typing again)
 * is instant with no network round-trip.
 *
 * @param {string} prefix - Power number prefix to search (min 2 chars)
 * @param {string|number} [agencyId] - Optional agency filter
 * @returns {Promise<{success: boolean, data?: {powers: Array}, error?: string}>}
 */
export const searchEligiblePowers = async (prefix, agencyId) => {
  const cacheKey = getSearchCacheKey(prefix, agencyId);
  const cached = _searchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < SEARCH_CACHE_TTL_MS) {
    return cached.result;
  }

  if (USE_MOCK) {
    await mockDelay(200);
    const upper = prefix.toUpperCase();
    const powers = MOCK_POWERS
      .filter((p) => p.powerNo.toUpperCase().startsWith(upper))
      .slice(0, 50)
      .map((p) => ({
        powerNo: p.powerNo,
        status: p.powerStatus,
        bondAmount: p.bondAmount,
        defendantName: p.defendantName,
        state: p.state,
        county: p.county,
        isForfeiture: p.isForfeiture,
        batchClosed: p.batchClosed,
      }));
    const result = { success: true, data: { powers } };
    _searchCache.set(cacheKey, { result, timestamp: Date.now() });
    return result;
  }

  try {
    const params = new URLSearchParams({ powerNo: prefix, take: '50' });
    if (agencyId) params.set('agencyId', agencyId);
    const response = await authFetch(`${BASE_URL}oath/discharge/powers?${params}`);
    const parsed = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: parsed.message || 'Search failed', data: null };
    }

    const powers = parsed.powers || parsed.data?.powers || [];
    const result = { success: true, data: { powers } };

    // Evict oldest entry if at capacity
    if (_searchCache.size >= SEARCH_CACHE_MAX) {
      const oldest = _searchCache.keys().next().value;
      _searchCache.delete(oldest);
    }
    _searchCache.set(cacheKey, { result, timestamp: Date.now() });

    return result;
  } catch (error) {
    console.error('Search eligible powers error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// VALIDATE POWERS
// ============================================

/**
 * Validate powers before discharge
 * @param {string[]} powerNumbers - Array of power numbers to validate
 * @param {string} [reason] - Optional reason for discharge
 * @param {string} [agentComments] - Optional agent comments
 * @param {Object} [options]
 * @param {number} [options.agencyId] - Agency ID for validation context
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const validatePowers = async (powerNumbers, reason, agentComments, { agencyId, excludeCallersQueue } = {}) => {
  if (USE_MOCK) {
    await mockDelay();
    const validations = powerNumbers.map(generateMockValidation);
    const validCount = validations.filter((v) => v.isValid && !v.requiresProof).length;
    const requiresProofCount = validations.filter((v) => v.isValid && v.requiresProof).length;
    const invalidCount = validations.filter((v) => !v.isValid).length;
    return {
      success: true,
      data: { success: true, validCount, requiresProofCount, invalidCount, validations },
    };
  }

  try {
    const response = await authFetch(`${BASE_URL}oath/discharge/validate`, {
      method: 'POST',
      body: JSON.stringify({ powerNumbers, reason, agentComments, agencyId, excludeCallersQueue }),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to validate powers', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Validate powers error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// SUBMIT DISCHARGE
// ============================================

/**
 * Submit discharge request
 * @param {Object} request - Discharge request
 * @param {string[]} request.powerNumbers - Powers to discharge (ignored when reportId is set)
 * @param {number} request.agencyId - Agency ID (required)
 * @param {number} [request.reportId] - If set, submits this draft (powers loaded from draft)
 * @param {string} [request.reason] - Reason for discharge
 * @param {string} [request.agentComments] - Agent comments
 * @param {string[]} [request.proofDocumentIds] - Uploaded proof file IDs
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const submitDischarge = async (request) => {
  if (USE_MOCK) {
    await mockDelay(1200);
    const { powerNumbers } = request;
    const validations = powerNumbers.map(generateMockValidation);
    const discharged = validations.filter((v) => v.isValid && !v.requiresProof).map((v) => v.powerNo);
    const pending = validations.filter((v) => v.isValid && v.requiresProof).map((v) => v.powerNo);
    const errors = validations
      .filter((v) => !v.isValid)
      .map((v) => ({
        powerNo: v.powerNo,
        message: v.validationErrors[0] || 'Validation failed',
        errorCode: 'VALIDATION_FAILED',
        requiresProof: false,
        proofsCount: 0,
      }));

    const totalLiability = validations
      .filter((v) => v.isValid)
      .reduce((sum, v) => sum + v.bondAmount, 0);

    return {
      success: true,
      data: {
        success: true,
        reportNumber: `${Date.now()}_DR_${Math.random().toString(36).slice(2, 10)}`,
        processedCount: discharged.length,
        pendingReviewCount: pending.length,
        totalLiability,
        dischargedPowers: discharged,
        pendingPowers: pending,
        errors,
        message: `${discharged.length} power(s) successfully discharged.${pending.length > 0 ? ` ${pending.length} power(s) pending review.` : ''}`,
      },
    };
  }

  try {
    const response = await authFetch(`${BASE_URL}oath/discharge/submit`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to submit discharge', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Submit discharge error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// DISCHARGE HISTORY
// ============================================

/**
 * Get discharge history (paginated)
 * @param {number} [pageNumber=1] - Page number
 * @param {number} [pageSize=20] - Items per page (max 100)
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getHistory = async (pageNumber = 1, pageSize = 20) => {
  if (USE_MOCK) {
    await mockDelay(400);
    const statuses = ['Completed', 'Partially Completed', 'Pending Proof', 'Failed', 'Completed'];
    const mockItems = Array.from({ length: 5 }, (_, i) => {
      const powerCount = Math.floor(Math.random() * 5) + 1;
      const discharged = statuses[i] === 'Completed' ? powerCount
        : statuses[i] === 'Partially Completed' ? Math.floor(powerCount / 2)
        : 0;
      const pendingProof = statuses[i] === 'Pending Proof' ? powerCount
        : statuses[i] === 'Partially Completed' ? powerCount - discharged
        : 0;
      return {
        reportNumber: `MOCK_DR_${1000 + i}`,
        submittedDate: new Date(Date.now() - i * 86400000 * 3).toISOString(),
        powerCount,
        dischargedCount: discharged,
        pendingProofCount: pendingProof,
        totalLiability: Math.floor(Math.random() * 80000) + 5000,
        isSuccess: statuses[i] !== 'Failed',
        status: statuses[i],
      };
    });
    return {
      success: true,
      data: {
        items: mockItems,
        totalCount: 5,
        pageNumber,
        pageSize,
        totalPages: 1,
      },
    };
  }

  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    const response = await authFetch(`${BASE_URL}oath/discharge/history?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch history', data: null };
    }

    return { success: true, data: result.data || result };
  } catch (error) {
    console.error('Get history error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// DISCHARGE STATUS
// ============================================

/**
 * Get specific discharge status
 * @param {number} reportId - The discharge report ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getStatus = async (reportId) => {
  if (USE_MOCK) {
    await mockDelay(400);
    return {
      success: true,
      data: {
        reportId,
        reportNumber: `MOCK_DR_${reportId}`,
        status: 'Partially Completed',
        submittedDate: new Date().toISOString(),
        powerCount: 3,
        totalLiability: 80000,
        powers: [
          { powerNo: 'FL-E-001', status: 'D', bondAmount: 5000, defendantName: 'Smith, John', isPending: false, hasProofUploaded: false, proofCount: 0 },
          { powerNo: 'TX-F-005', status: 'F', bondAmount: 25000, defendantName: 'Davis, Michael', isPending: true, hasProofUploaded: true, proofCount: 2 },
          { powerNo: 'FL-J-007', status: 'J', bondAmount: 50000, defendantName: 'Martinez, Carlos', isPending: true, hasProofUploaded: false, proofCount: 0 },
        ],
        reviewedBy: null,
        reviewedDate: null,
        isSuccess: true,
        errorMessage: null,
      },
    };
  }

  try {
    const response = await authFetch(`${BASE_URL}oath/discharge/detail?reportId=${reportId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch status', data: null };
    }

    return { success: true, data: result.data || result };
  } catch (error) {
    console.error('Get status error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// PROOF UPLOAD / DELETE
// ============================================

/**
 * Extract the file-level ID from a blobName that may include a powerNo prefix.
 * e.g. "520027495/9a5f8772-7317-4397-b28b-fb992de38845.jpg" → "9a5f8772-7317-4397-b28b-fb992de38845.jpg"
 * If there's no slash, returns the input unchanged.
 */
const extractFileId = (blobNameOrFileId) => {
  if (!blobNameOrFileId) return blobNameOrFileId;
  const slashIdx = blobNameOrFileId.lastIndexOf('/');
  return slashIdx >= 0 ? blobNameOrFileId.substring(slashIdx + 1) : blobNameOrFileId;
};

/**
 * Upload proof document
 * @param {string} powerNo - Power number
 * @param {File} file - File to upload
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const uploadProof = async (powerNo, file) => {
  if (USE_MOCK) {
    await mockDelay(800);
    mockFileCounter++;
    return {
      success: true,
      data: {
        success: true,
        fileId: `${powerNo}_${mockFileCounter}_${Math.random().toString(36).slice(2, 10)}.${file.name.split('.').pop()}`,
        fileName: file.name,
        powerNo,
      },
    };
  }

  try {
    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BASE_URL}oath/discharge/${encodeURIComponent(powerNo)}/proof`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.status === 401) {
      const refreshResult = await oathRefreshToken();
      if (refreshResult.success) {
        const newToken = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
        const retryResponse = await fetch(`${BASE_URL}oath/discharge/${encodeURIComponent(powerNo)}/proof`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${newToken}`,
          },
          body: formData,
        });
        const retryResult = await retryResponse.json().catch(() => ({}));
        const retryData = retryResult.result !== undefined ? retryResult.result : retryResult;
        if (!retryResponse.ok) {
          return { success: false, error: retryData.message || 'Failed to upload proof', data: null };
        }
        return { success: true, data: retryData };
      }
    }

    const result = await response.json().catch(() => ({}));
    const data = result.result !== undefined ? result.result : result;

    if (!response.ok) {
      return { success: false, error: data.message || 'Failed to upload proof', data: null };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Upload proof error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Delete proof document
 * @param {string} powerNo - Power number
 * @param {string} fileId - File ID to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteProof = async (powerNo, fileId) => {
  if (USE_MOCK) {
    await mockDelay(300);
    return { success: true };
  }

  try {
    const bareFileId = extractFileId(fileId);
    const response = await authFetch(
      `${BASE_URL}oath/discharge/${encodeURIComponent(powerNo)}/proofs/${encodeURIComponent(bareFileId)}`,
      { method: 'DELETE' }
    );

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to delete proof' };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete proof error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * List uploaded proofs for a power
 * @param {string} powerNo - Power number
 * @returns {Promise<{success: boolean, data?: Array<{fileId: string, fileName: string}>, error?: string}>}
 */
export const getProofs = async (powerNo) => {
  if (USE_MOCK) {
    await mockDelay(200);
    return { success: true, data: [] };
  }

  try {
    const response = await authFetch(
      `${BASE_URL}oath/discharge/${encodeURIComponent(powerNo)}/proofs`,
    );
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch proofs', data: null };
    }

    return { success: true, data: Array.isArray(result) ? result : result.proofs || [] };
  } catch (error) {
    console.error('Get proofs error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Download/view a proof document
 * Returns the file as a blob with content type for preview or download.
 * @param {string} powerNo - Power number
 * @param {string} fileId - File ID (e.g., "a1b2c3d4-e5f6.pdf")
 * @returns {Promise<{success: boolean, data?: {blob: Blob, contentType: string, fileName: string}, error?: string}>}
 */
export const downloadProof = async (powerNo, fileId) => {
  if (USE_MOCK) {
    await mockDelay(300);
    // Return a tiny placeholder blob for mock mode
    const blob = new Blob(['Mock proof content'], { type: 'application/pdf' });
    return { success: true, data: { blob, contentType: 'application/pdf', fileName: fileId } };
  }

  try {
    const bareFileId = extractFileId(fileId);
    const response = await authFetch(
      `${BASE_URL}oath/discharge/${encodeURIComponent(powerNo)}/proofs/${encodeURIComponent(bareFileId)}`,
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      console.error('Download proof failed:', response.status, errorText);
      return { success: false, error: `Failed to download proof (${response.status})`, data: null };
    }

    const blob = await response.blob();
    const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
    return { success: true, data: { blob, contentType, fileName: fileId } };
  } catch (error) {
    console.error('Download proof error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Complete a discharge — re-validates pending powers that now have proof and discharges them
 * @param {string} reportNumber - The discharge report number
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const completeDischarge = async (reportId) => {
  if (USE_MOCK) {
    await mockDelay(800);
    return {
      success: true,
      data: {
        success: true,
        reportId,
        processedCount: 1,
        pendingReviewCount: 0,
        totalLiability: 25000,
        dischargedPowers: ['TX-F-005'],
        pendingPowers: ['FL-J-007'],
        errors: [],
        message: '1 power(s) newly discharged. 1 power(s) still pending proof.',
      },
    };
  }

  try {
    const response = await authFetch(
      `${BASE_URL}oath/discharge/complete?reportId=${reportId}`,
      { method: 'POST' },
    );
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to complete discharge', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Complete discharge error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// REMOVE POWER FROM REPORT
// ============================================

/**
 * Remove a validation-failed power from a discharge report.
 * The backend re-validates the power — only removes if it fails re-validation.
 * @param {number} reportId - The discharge report ID
 * @param {string} powerNo - Power number to remove
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const removePowerFromReport = async (reportId, powerNo) => {
  if (USE_MOCK) {
    await mockDelay(400);
    return {
      success: true,
      data: { success: true, message: `Power ${powerNo} removed from report ${reportId}` },
    };
  }

  try {
    const response = await authFetch(
      `${BASE_URL}oath/discharge/powers/${encodeURIComponent(powerNo)}?reportId=${reportId}`,
      { method: 'DELETE' },
    );
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to remove power from report', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Remove power from report error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// DISCHARGE QUEUE (Persistence)
// ============================================

/**
 * Get all queued (unsubmitted) discharge powers
 * @param {Object} [options]
 * @param {number} [options.reportId] - If set, returns items for that draft; otherwise unassigned items
 * @returns {Promise<{success: boolean, data?: {items: Array, totalCount: number, totalLiability: number}, error?: string}>}
 */
export const getDischargeQueue = async ({ reportId } = {}) => {
  if (USE_MOCK) {
    await mockDelay(300);
    return { success: true, data: { items: [], totalCount: 0, totalLiability: 0 } };
  }

  try {
    const url = new URL(`${BASE_URL}oath/discharge/queue`);
    if (reportId != null) url.searchParams.set('reportId', reportId);

    const response = await authFetch(url.toString());
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch queue', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get discharge queue error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Add a single power to the discharge queue
 * @param {string} powerNumber - Power number to add
 * @param {Object} [options]
 * @param {number} options.agencyId - Agency ID (required by API)
 * @param {number} [options.reportId] - If set, adds directly to that draft
 * @param {string} [options.defendantName] - Optional defendant name for display
 * @param {number} [options.liability] - Optional bond amount
 * @param {string} [options.status] - Optional power status code
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addToQueue = async (powerNumber, { agencyId, reportId, defendantName, liability, status, type } = {}) => {
  if (USE_MOCK) {
    await mockDelay(100);
    const v = generateMockValidation(powerNumber);
    return { success: true, data: { validation: v } };
  }

  try {
    const url = new URL(`${BASE_URL}oath/discharge/queue`);
    if (reportId != null) url.searchParams.set('reportId', reportId);

    const body = { powerNumber, agencyId };
    if (defendantName) body.defendantName = defendantName;
    if (liability != null) body.liability = liability;
    if (status) body.status = status;
    if (type) body.type = type;

    const response = await authFetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to add to queue' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Add to queue error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Add multiple powers to the discharge queue
 * @param {Array<string|Object>} powers - Power numbers (strings) or objects with details
 *   When objects: { powerNo, defendantName?, bondAmount?, status? }
 * @param {Object} [options]
 * @param {number} options.agencyId - Agency ID (required by API, top-level field)
 * @param {number} [options.reportId] - If set, adds directly to that draft
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addBulkToQueue = async (powers, { agencyId, reportId } = {}) => {
  if (USE_MOCK) {
    await mockDelay(150);
    const validations = powers.map((p) => {
      const no = typeof p === 'string' ? p : p.powerNo || p.powerNumber;
      return generateMockValidation(no);
    });
    return {
      success: true,
      data: {
        validations,
        addedCount: validations.filter((v) => v.isValid).length,
        invalidCount: validations.filter((v) => !v.isValid).length,
      },
    };
  }

  try {
    const payload = powers.map((p) => {
      if (typeof p === 'string') return { powerNumber: p };
      return {
        powerNumber: p.powerNo || p.powerNumber,
        agencyId,
        ...(p.defendantName && { defendantName: p.defendantName }),
        ...(p.bondAmount != null && { liability: p.bondAmount }),
        ...(p.status && { status: p.status }),
        ...(p.type && { type: p.type }),
      };
    });

    const url = new URL(`${BASE_URL}oath/discharge/queue/bulk`);
    if (reportId != null) url.searchParams.set('reportId', reportId);

    const response = await authFetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify({ agencyId, powers: payload }),
    });
    const result = await parseResponse(response);

    if (!response.ok || result.success === false) {
      return { success: false, error: result.message || 'Failed to bulk add to queue', data: result };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Bulk add to queue error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Remove a single power from the discharge queue
 * @param {string} powerNumber - Power number to remove
 * @param {Object} [options]
 * @param {number} [options.reportId] - If set, removes from that draft
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const removeFromQueue = async (powerNumber, { reportId } = {}) => {
  if (USE_MOCK) {
    await mockDelay(100);
    return { success: true };
  }

  try {
    const url = new URL(`${BASE_URL}oath/discharge/queue/${encodeURIComponent(powerNumber)}`);
    if (reportId != null) url.searchParams.set('reportId', reportId);

    const response = await authFetch(url.toString(), { method: 'DELETE' });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to remove from queue' };
    }

    return { success: true };
  } catch (error) {
    console.error('Remove from queue error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Clear entire discharge queue
 * @param {Object} [options]
 * @param {number} [options.reportId] - If set, clears items for that draft
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const clearQueue = async ({ reportId } = {}) => {
  if (USE_MOCK) {
    await mockDelay(100);
    return { success: true };
  }

  try {
    const url = new URL(`${BASE_URL}oath/discharge/queue`);
    if (reportId != null) url.searchParams.set('reportId', reportId);

    const response = await authFetch(url.toString(), { method: 'DELETE' });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to clear queue' };
    }

    return { success: true };
  } catch (error) {
    console.error('Clear queue error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// DISCHARGE DRAFTS
// ============================================

/**
 * Create a new draft from unassigned queue items
 * @param {number} agencyId - Agency ID (required)
 * @returns {Promise<{success: boolean, data?: {reportId: number, reportNumber: string, powerCount: number, message?: string}, error?: string}>}
 */
export const createDraft = async (agencyId) => {
  if (USE_MOCK) {
    await mockDelay(400);
    return {
      success: true,
      data: {
        reportId: Date.now(),
        reportNumber: `${agencyId}_DR_${Math.random().toString(36).slice(2, 10)}`,
        powerCount: 0,
        message: 'Draft saved successfully',
      },
    };
  }

  try {
    const response = await authFetch(`${BASE_URL}oath/discharge/drafts`, {
      method: 'POST',
      body: JSON.stringify({ agencyId }),
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to create draft', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Create draft error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * List all open drafts for the current user's agencies
 * @returns {Promise<{success: boolean, data?: Array<{reportId: number, reportNumber: string, agentId: number, agencyName?: string, dateCreated: string, powerCount: number, totalLiability: number}>, error?: string}>}
 */
export const getDrafts = async () => {
  if (USE_MOCK) {
    await mockDelay(300);
    return { success: true, data: [] };
  }

  try {
    const response = await authFetch(`${BASE_URL}oath/discharge/drafts`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch drafts', data: null };
    }

    // API returns { drafts: [...] } — normalize to just the array
    const drafts = result.drafts || result;
    return { success: true, data: Array.isArray(drafts) ? drafts : [] };
  } catch (error) {
    console.error('Get drafts error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Get a single draft with all its queue items
 * @param {number} reportId - Draft report ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getDraft = async (reportId) => {
  if (USE_MOCK) {
    await mockDelay(300);
    return {
      success: true,
      data: {
        reportId,
        reportNumber: `MOCK_DR_${reportId}`,
        agentId: 100,
        agencyName: 'Mock Agency',
        dateCreated: new Date().toISOString(),
        powerCount: 0,
        totalLiability: 0,
        items: [],
      },
    };
  }

  try {
    const response = await authFetch(`${BASE_URL}oath/discharge/drafts/${reportId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch draft', data: null };
    }

    // API returns { draft: {...} } — normalize
    return { success: true, data: result.draft || result };
  } catch (error) {
    console.error('Get draft error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Abandon (delete) a draft and all its queue items
 * @param {number} reportId - Draft report ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const abandonDraft = async (reportId) => {
  if (USE_MOCK) {
    await mockDelay(200);
    return { success: true };
  }

  try {
    const response = await authFetch(`${BASE_URL}oath/discharge/drafts/${reportId}`, {
      method: 'DELETE',
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to abandon draft' };
    }

    return { success: true };
  } catch (error) {
    console.error('Abandon draft error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// MOCK POWERS (for development — sourcing powers)
// ============================================

/**
 * Get mock powers for development.
 * In production, powers are sourced from Power Inquiry / open liability data.
 * @returns {Promise<{success: boolean, data: Array}>}
 */
export const getMockPowers = async () => {
  if (USE_MOCK) {
    await mockDelay(500);
    return { success: true, data: MOCK_POWERS };
  }
  return { success: true, data: [] };
};
